import { observer } from 'mobx-react';
import { ExpandMore } from '@material-ui/icons';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Grid,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { colorByStatus } from 'libs/feature-loan-details/src/loanDetails/components/todoStatusChip';
import { Button } from '@roc/ui';
import { TodoStatus } from 'libs/feature-loan-details/src/loanDetails/utils/loanDetailsTypes';
import { useEffect, useState } from 'react';
import { IdVerificationSummary } from '../../withIdVerification/idVerificationSummary';
import { FinanciallInformation } from '../../financialInformation';
import { IdAndFinancialInformationSteps } from './idAndFinancialInformationSteps';
import { FinancialInformationStep } from '../../../../identityCheck/steps/financialInformationStep';
import { IdInformationStep } from '../../../../identityCheck/steps/idInformationStep';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useUserStore } from '@roc/feature-app-core';

const useStyles = makeStyles<Theme, { status }>((theme: Theme) => ({
  chipColor: props => ({
    color: 'white',
    borderColor: colorByStatus[props.status],
    backgroundColor: colorByStatus[props.status],
  }),
}));

export const StatusChip = observer(props => {
  const { status } = props;
  const classes = useStyles({ status });

  return <Chip label={status} className={classes.chipColor} size="small" />;
});

export const IdAndFinancialInformationSections = observer(props => {
  const { store } = props;
  const { currentBorrower } = store;
  const elegibleForRepull =
    currentBorrower &&
    currentBorrower?.areCreditScoresFrozen &&
    currentBorrower?.cafSignedDate;
  const { identityCheckStore } = useStore();
  const { userStore } = useUserStore();
  const { isNportalUser } = userStore;

  const [isIDInformationExpanded, setIsIDInformationExpanded] = useState(false);
  const [
    isFinancialInformationExpanded,
    setIsFinancialInformationExpanded,
  ] = useState(false);

  useEffect(() => {
    store.setIsCreditInformationExpanded(elegibleForRepull);
  }, [elegibleForRepull]);

  return (
    <Grid item xs={12}>
      <Accordion
        expanded={isIDInformationExpanded}
        onChange={(event: React.SyntheticEvent, expanded: boolean) =>
          setIsIDInformationExpanded(expanded)
        }
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Grid container justifyContent="space-between">
            <Grid item>ID Information</Grid>
            <Grid item>
              <StatusChip status={identityCheckStore.idInformationStatus} />
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <IdInformationStep />
        </AccordionDetails>
        <AccordionActions>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              const result = await identityCheckStore.saveIdInformation();
              if (result) {
                setIsIDInformationExpanded(false);
                setIsFinancialInformationExpanded(true);
              }
            }}
            testId="saveIdInformation"
            disabled={!isNportalUser}
          >
            Save
          </Button>
        </AccordionActions>
      </Accordion>
      <Accordion
        expanded={isFinancialInformationExpanded}
        onChange={(event: React.SyntheticEvent, expanded: boolean) =>
          setIsFinancialInformationExpanded(expanded)
        }
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Grid container justifyContent="space-between">
            <Grid item>Financial Information</Grid>
            <Grid item>
              <StatusChip
                status={identityCheckStore.financialInformationStatus}
              />
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <FinancialInformationStep />
        </AccordionDetails>
        <AccordionActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => identityCheckStore.saveFinancialInformation()}
            testId="saveFinancialInformation"
            disabled={!isNportalUser}
          >
            Save
          </Button>
        </AccordionActions>
      </Accordion>
    </Grid>
  );
});
